import axios from 'axios'
import { useAuthStore } from '../stores/authStore.js'

console.log('VITE_API_URL:', import.meta.env.VITE_API_URL)
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

// Scraper Python service (FastAPI déployé sur Coolify — pas d'auth requise)
const SCRAPER_API_URL = import.meta.env.VITE_SCRAPER_API_URL || 'https://bko8o4ww4k8cg0ckkwkgssco.usts.ai'
const scraperApi = axios.create({ baseURL: SCRAPER_API_URL })

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

const AUTO_PROJECT_API_KEY = import.meta.env.VITE_AUTO_PROJECT_API_KEY || ''
const FRANCE_TRAVAIL_CANDIDATE_API_KEY = import.meta.env.VITE_FRANCE_TRAVAIL_API_KEY || AUTO_PROJECT_API_KEY || ''

export const healthCheck = async () => {
  const { data } = await api.get('/api/health')
  return data
}

export const getFranceTravailCandidates = async ({ page = 1, itemsPerPage = 100 } = {}) => {
  const { data } = await api.get('/api/france-travail-candidates', {
    params: {
      page,
      itemsPerPage,
      'order[createdAt]': 'desc',
    },
    headers: {
      'x-api-key': FRANCE_TRAVAIL_CANDIDATE_API_KEY,
    },
  })

  return data
}

export const createFranceTravailCandidate = async (payload) => {
  const { data } = await api.post('/api/france-travail-candidates/n8n', payload, {
    headers: {
      'x-api-key': FRANCE_TRAVAIL_CANDIDATE_API_KEY,
    },
  })

  return data
}

export const updateFranceTravailCandidate = async (id, payload) => {
  const token = useAuthStore.getState().token
  const { data } = await api.patch(
    `/api/france-travail-candidates/${id}`,
    payload,
    {
      headers: {
        'Content-Type': 'application/merge-patch+json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  )
  return data
}

export const searchFranceTravail = async ({ keywords, location, userId, account = null, maxProfiles = null }) => {
  const payload = { keywords, location, user_id: userId ?? null }
  if (maxProfiles) {
    payload.max_profiles = maxProfiles
  }
  if (account) {
    payload.account = account
  }
  const { data } = await api.post(
    '/api/france-travail-candidates/search',
    payload,
    {
      headers: {
        'x-api-key': FRANCE_TRAVAIL_CANDIDATE_API_KEY,
      },
    }
  )

  return data
}

// Demande POEI : déposée dans la file du worker par l'API (JWT de l'utilisateur).
export const submitPoei = async (payload) => {
  const { data } = await api.post('/api/france-travail-candidates/poei', payload)
  return data
}

// CV d'un candidat France Travail (stocké par le worker, relu par l'API)
export const getFranceTravailCandidateCv = async (id) => {
  const { data } = await api.get(`/api/france-travail-candidates/${id}/cv`, { responseType: 'blob' })
  return data
}

// Documents d'un dossier prospect (onglet Documents) et modèles de l'organisation (Compte > Modèles).
export const getProspectDocuments = async (prospectId) => {
  const { data } = await api.get(`/api/prospects/${prospectId}/documents`)
  return data.documents
}

export const uploadProspectDocument = async (prospectId, file, { kind = 'other', title } = {}) => {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  if (title) form.append('title', title)
  const { data } = await api.post(`/api/prospects/${prospectId}/documents`, form)
  return data.document
}

export const generateProspectDocument = async (prospectId, { templateId, autoProjectId } = {}) => {
  const { data } = await api.post(`/api/prospects/${prospectId}/documents/generate`, { templateId, autoProjectId })
  return data
}

export const getDocumentFile = async (documentId) => {
  const { data } = await api.get(`/api/documents/${documentId}/download`, { responseType: 'blob' })
  return data
}

export const updateDocument = async (documentId, changes) => {
  const { data } = await api.patch(`/api/documents/${documentId}`, changes)
  return data.document
}

export const deleteDocument = async (documentId) => {
  await api.delete(`/api/documents/${documentId}`)
}

export const shareDocument = async (documentId, days = null) => {
  const { data } = await api.post(`/api/documents/${documentId}/share`, { days })
  return data.document
}

export const unshareDocument = async (documentId) => {
  const { data } = await api.delete(`/api/documents/${documentId}/share`)
  return data.document
}

export const getDocumentTemplates = async () => {
  const { data } = await api.get('/api/document-templates')
  return data
}

export const createDocumentTemplate = async (template) => {
  const { data } = await api.post('/api/document-templates', template)
  return data.template
}

export const updateDocumentTemplate = async (id, changes) => {
  const { data } = await api.put(`/api/document-templates/${id}`, changes)
  return data.template
}

export const deleteDocumentTemplate = async (id) => {
  await api.delete(`/api/document-templates/${id}`)
}

export const setDefaultDocumentTemplate = async (id) => {
  const { data } = await api.post(`/api/document-templates/${id}/default`)
  return data.templates
}

export const duplicateDocumentTemplate = async (id) => {
  const { data } = await api.post(`/api/document-templates/${id}/duplicate`)
  return data.template
}

export const resetDocumentTemplate = async (id) => {
  const { data } = await api.post(`/api/document-templates/${id}/reset`)
  return data.template
}

// Suivi d'une tâche de fond (recherche France Travail, POEI…) : statut, progression, résultat.
export const getWorkerJob = async (id) => {
  const { data } = await api.get(`/api/worker-jobs/${id}`)
  return data
}

// Compte > Authentification : identifiants des services externes (LinkedIn…), chiffrés côté API.
export const getServiceCredentials = async () => {
  const { data } = await api.get('/api/credentials')
  return data
}

export const saveServiceCredential = async ({ provider, login, password, acceptRisk }) => {
  const { data } = await api.put(`/api/credentials/${provider}`, { login, password, acceptRisk })
  return data
}

export const sendServiceCredentialCode = async ({ provider, code }) => {
  const { data } = await api.post(`/api/credentials/${provider}/code`, { code })
  return data
}

export const reconnectServiceCredential = async (provider) => {
  const { data } = await api.post(`/api/credentials/${provider}/reconnect`)
  return data
}

// Connexion par navigateur à distance : l'utilisateur se connecte lui-même dans une fenêtre JLP.
export const startBrowserLogin = async ({ provider, acceptRisk }) => {
  const { data } = await api.post(`/api/credentials/${provider}/browser-login`, { acceptRisk })
  return data
}

export const cancelBrowserLogin = async (provider) => {
  const { data } = await api.post(`/api/credentials/${provider}/browser-login/cancel`)
  return data
}

export const deleteServiceCredential = async (provider) => {
  const { data } = await api.delete(`/api/credentials/${provider}`)
  return data
}

// Prospection LinkedIn (compte LinkedIn de l'utilisateur) : recherche dans le worker, puis ajout au CRM.
export const searchLinkedin = async ({ keywords, location, maxResults }) => {
  const { data } = await api.post('/api/linkedin/search', { keywords, location, maxResults })
  return data
}

export const importLinkedinProfiles = async ({ jobId, profileUrls }) => {
  const { data } = await api.post(`/api/linkedin/searches/${jobId}/import`, { profileUrls })
  return data
}

// Menu Contrats : devis préparés dans JLP et créés dans Pennylane, factures Pennylane.
export const getQuotes = async () => (await api.get('/api/quotes')).data
export const getQuote = async (id) => (await api.get(`/api/quotes/${id}`)).data
export const createQuote = async (payload) => (await api.post('/api/quotes', payload)).data
// Facturation d'un projet auto (Pennylane de son agence) : aperçu, envoi, renvoi d'une facture.
export const getAutoProjectBilling = async (projectId) => (await api.get(`/api/billing/auto-project/${encodeURIComponent(projectId)}`)).data
export const sendAutoProjectBilling = async ({ projectId, recipient }) =>
  (await api.post(`/api/billing/auto-project/${encodeURIComponent(projectId)}/send`, { recipient })).data
export const resendAutoProjectBilling = async ({ projectId, billingId }) =>
  (await api.post(`/api/billing/auto-project/${encodeURIComponent(projectId)}/billings/${billingId}/resend`)).data

// Projet auto : son devis et son contrat ; POST crée le devis depuis le client et l'offre du projet.
export const getAutoProjectDocuments = async (projectId) => (await api.get(`/api/quotes/auto-project/${encodeURIComponent(projectId)}`)).data
export const createAutoProjectQuote = async (projectId) => (await api.post(`/api/quotes/auto-project/${encodeURIComponent(projectId)}`)).data
export const updateQuote = async ({ id, ...fields }) => (await api.patch(`/api/quotes/${id}`, fields)).data
export const deleteQuote = async (id) => (await api.delete(`/api/quotes/${id}`)).data
export const generateQuote = async ({ id, instructions }) => (await api.post(`/api/quotes/${id}/generate`, { instructions })).data
export const applyQuoteGeneration = async (id) => (await api.post(`/api/quotes/${id}/apply-generation`)).data
export const pushQuoteToPennylane = async (id) => (await api.post(`/api/quotes/${id}/pennylane`)).data
export const syncQuote = async (id) => (await api.post(`/api/quotes/${id}/sync`)).data
export const setQuoteStatus = async ({ id, status }) => (await api.post(`/api/quotes/${id}/status`, { status })).data
export const createInvoiceFromQuote = async (id) => (await api.post(`/api/quotes/${id}/invoice`)).data
export const finalizeQuoteInvoice = async (id) => (await api.post(`/api/quotes/${id}/invoice/finalize`)).data
export const getQuotePdfUrl = async ({ id, document }) => (await api.get(`/api/quotes/${id}/pdf/${document}`)).data
export const getQuoteOffers = async () => (await api.get('/api/quotes/offers')).data
export const importQuoteSerge = async (id) => (await api.post(`/api/quotes/${id}/import-serge`)).data
// Factures Pennylane d'une entreprise (par défaut la première qui a sa clé).
export const getInvoices = async (cursor = null, companyId = null) =>
  (await api.get('/api/invoices', { params: { cursor: cursor || undefined, companyId: companyId || undefined } })).data

// Menu Contrats > Contrats : contrat créé depuis un devis, généré en .docx + PDF par le worker.
export const getContracts = async () => (await api.get('/api/contracts')).data
export const getContract = async (id) => (await api.get(`/api/contracts/${id}`)).data
export const createContract = async (payload) => (await api.post('/api/contracts', payload)).data
export const updateContract = async ({ id, ...fields }) => (await api.patch(`/api/contracts/${id}`, fields)).data
export const deleteContract = async (id) => (await api.delete(`/api/contracts/${id}`)).data
export const generateContract = async (id) => (await api.post(`/api/contracts/${id}/generate`)).data
export const getContractFile = async ({ id, format }) =>
  (await api.get(`/api/contracts/${id}/file/${format}`, { responseType: 'blob' })).data

// Compte > Entreprises : les sociétés « Prestataire » des contrats (org ou utilisateur seul).
export const getMyCompanies = async () => (await api.get('/api/my-companies')).data
export const createMyCompany = async (payload) => (await api.post('/api/my-companies', payload)).data
export const updateMyCompany = async ({ id, ...fields }) => (await api.patch(`/api/my-companies/${id}`, fields)).data
export const deleteMyCompany = async (id) => (await api.delete(`/api/my-companies/${id}`)).data
// Clé Pennylane de l'entreprise : vérifiée par l'API, jamais renvoyée.
export const saveMyCompanyPennylaneKey = async ({ id, apiKey }) => (await api.put(`/api/my-companies/${id}/pennylane`, { apiKey })).data
export const removeMyCompanyPennylaneKey = async (id) => (await api.delete(`/api/my-companies/${id}/pennylane`)).data

// « Connecter Gmail » : URL Google qui autorise l'envoi d'e-mails (séparé de la connexion).
export const connectGmail = async (returnTo) => (await api.post('/api/gmail/connect', { returnTo })).data

export const saveServiceApiKey = async ({ provider, apiKey }) => {
  const { data } = await api.put(`/api/credentials/${provider}/api-key`, { apiKey })
  return data
}

// token : juste après /api/login, avant qu'il soit enregistré dans authStore.
export const getCurrentUser = async (token = null) => {
  const { data } = await api.get('/api/auth/me', token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
  return data
}

// Statistiques globales de l'espace admin (ROLE_ADMIN requis côté API).
export const getAdminStats = async () => {
  const { data } = await api.get('/api/admin/stats')
  return data.stats
}

// Vue manager consolidée (pipeline + équipe + blocages).
export const getManagerOverview = async () => {
  const { data } = await api.get('/api/admin/manager')
  return data.data
}

// Import / export CSV.
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
const isoDay = () => new Date().toISOString().slice(0, 10)

export const exportProspectsCsv = async (ids = null) => {
  const { data } = await api.post('/api/prospects/export', ids ? { ids } : {}, { responseType: 'blob' })
  triggerDownload(data, `prospects_${isoDay()}.csv`)
}
export const importProspectsCsv = async (file) => {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post('/api/prospects/import', fd)
  return data.report
}
export const exportAutoProjectsCsv = async (ids = null) => {
  const { data } = await api.post('/api/auto-projects/export', ids ? { ids } : {}, {
    responseType: 'blob',
    headers: { 'x-api-key': AUTO_PROJECT_API_KEY },
  })
  triggerDownload(data, `projets_auto_${isoDay()}.csv`)
}

// Suivi des commissions.
export const getAdminCommissions = async () => {
  const { data } = await api.get('/api/admin/commissions')
  return data
}
export const getMyCommissions = async () => {
  const { data } = await api.get('/api/commissions/me')
  return data
}

// Bibliothèque de templates commerciaux partagés (CRUD admin).
export const getAdminEmailTemplates = async () => {
  const { data } = await api.get('/api/admin/email-templates')
  return data.templates
}
export const createAdminEmailTemplate = async (payload) => {
  const { data } = await api.post('/api/admin/email-templates', payload)
  return data.template
}
export const updateAdminEmailTemplate = async (id, payload) => {
  const { data } = await api.put(`/api/admin/email-templates/${id}`, payload)
  return data.template
}
export const deleteAdminEmailTemplate = async (id) => {
  await api.delete(`/api/admin/email-templates/${id}`)
}

// Vue risques & blocages (opportunités à risque ou bloquées).
export const getRisks = async () => {
  const { data } = await api.get('/api/admin/risks')
  return data.data
}

// Gestion des utilisateurs (ROLE_ADMIN requis côté API).
export const getAdminUsers = async () => {
  const { data } = await api.get('/api/admin/users')
  return data.users
}

export const createAdminUser = async (payload) => {
  const { data } = await api.post('/api/admin/users', payload)
  return data.user
}

export const updateAdminUser = async (id, payload) => {
  const { data } = await api.put(`/api/admin/users/${id}`, payload)
  return data.user
}

export const deleteAdminUser = async (id) => {
  const { data } = await api.delete(`/api/admin/users/${id}`)
  return data
}

// Aperçu des données liées qui seront détruites par une suppression en cascade.
export const getAdminUserRelatedData = async (id) => {
  const { data } = await api.get(`/api/admin/users/${id}/related-data`)
  return data
}

// Fil d'activité admin (emails, statuts, relances, notes) — filtrable par commercial/type/période.
export const getAdminActivity = async (params = {}) => {
  const { data } = await api.get('/api/admin/activity', { params })
  return data.items
}

// Projets dormants du commercial connecté (admin : ?userId= pour cibler un commercial).
export const getDormantProjects = async (params = {}) => {
  const { data } = await api.get('/api/dormant-projects', { params })
  return data
}

// Annonces / notes de version — côté utilisateur.
export const getAnnouncements = async () => {
  const { data } = await api.get('/api/announcements')
  return data // { items, unseenCount }
}
export const markAnnouncementsSeen = async () => {
  await api.post('/api/announcements/mark-seen')
}

// Annonces — CRUD admin.
export const getAdminAnnouncements = async () => {
  const { data } = await api.get('/api/admin/announcements')
  return data.items
}
export const createAnnouncement = async (payload) => {
  const { data } = await api.post('/api/admin/announcements', payload)
  return data.announcement
}
export const updateAnnouncement = async (id, payload) => {
  const { data } = await api.put(`/api/admin/announcements/${id}`, payload)
  return data.announcement
}
export const deleteAnnouncement = async (id) => {
  await api.delete(`/api/admin/announcements/${id}`)
}

// Connexion utilisateur (email/password)
export const loginUser = async ({ email, password }) => {
  const { data } = await api.post('/api/login', { email, password })
  return data
}

// Inscription utilisateur (email/password)
export const registerUser = async ({ email, password, firstName = '', lastName = '' }) => {
  const { data } = await api.post('/api/users/register', { email, password, firstName, lastName })
  return data
}

export const getCreditBalance = async () => {
  const { data } = await api.get('/api/credits/balance')
  if (data == null || typeof data.credits !== 'number') {
    throw new Error('Réponse invalide de /api/credits/balance')
  }
  return data
}

export const verifyCreditPurchase = async (sessionId) => {
  const { data } = await api.get(`/api/credits/purchase/verify/${encodeURIComponent(sessionId)}`)
  return data
}

// Acheter des crédits (POC)
export const purchaseCredits = async (amount, packageId = null, price = 0) => {
  const payload = { amount, package_id: packageId, price }
  const { data } = await api.post('/api/credits/purchase', payload)
  return data
}

// Historique des crédits
export const getCreditHistory = async (limit = 50) => {
  const { data } = await api.get(`/api/credits/history`, { params: { limit } })
  return data
}

// Vérifier le solde requis
export const checkCredits = async (amount) => {
  const { data } = await api.post('/api/credits/check', { amount })
  return data
}

// Débiter des crédits (POC)
export const deductCredits = async (amount, description = null, metadata = {}) => {
  const { data } = await api.post('/api/credits/deduct', { amount, description, metadata })
  return data
}

// Dashboard stats
export const getDashboardStats = async () => {
  const { data } = await api.get('/api/dashboard/stats')
  return data
}

// Recherche B2B (worker prospects.search) : lance la recherche, puis suivre son état.
// État : { jobId, requestId, status, done, progress, error, warnings, criteria, total, preview, availableContactIds }
export const startProspectPreview = async (query, quantity) => {
  const { data } = await api.post('/api/prospects/preview', { query, quantity })
  return data
}

export const getProspectPreview = async (jobId) => {
  const { data } = await api.get(`/api/prospects/preview/${jobId}`)
  return data
}

export const getMyProspects = async () => {
  const { data } = await api.get('/api/user/prospects')
  return data
}

// Écran Prospects du CRM, paginé côté serveur (hors fiches des projets auto).
const crmParams = { paramsSerializer: { indexes: null } }
export const getCrmProspects = async (params) => {
  const { data } = await api.get('/api/crm/prospects', { params, ...crmParams })
  return data
}
export const getCrmProspectFacets = async () => {
  const { data } = await api.get('/api/crm/prospects/facets')
  return data
}
export const getCrmProspectIds = async (params) => {
  const { data } = await api.get('/api/crm/prospects/ids', { params, ...crmParams })
  return data.ids
}

export const localSearchProspects = async ({ jobTitle = '', location = '', sector = '' }) => {
  const { data } = await api.post('/api/prospects/local-search', { jobTitle, location, sector })
  return data
}

export const getAllProspectsFromDb = async () => {
  const { data } = await api.get('/api/prospects/all')
  return data
}

export const claimProspects = async (prospectIds) => {
  const { data } = await api.post('/api/prospects/claim', { prospectIds })
  return data
}

export const updateProspect = async (id, fields) => {
  const { data } = await api.patch(`/api/prospects/${id}`, fields, {
    headers: { 'Content-Type': 'application/json' },
  })
  return data
}

export const assignProspectSalesRep = async (id, userId) => {
  const { data } = await api.patch(`/api/prospects/${id}/assigned-sales-rep`, { userId }, {
    headers: { 'Content-Type': 'application/json' },
  })
  return data
}

// ============================
// Communication - Recipients
// ============================

export const getRecipients = async (options = {}) => {
  const { page = 1, limit = 50, q = null } = options
  const params = { page, limit }
  if (q) params.q = q
  const { data } = await api.get('/api/recipients', { params })
  return data
}

export const createRecipient = async (payload) => {
  const { data } = await api.post('/api/recipients', payload)
  return data
}

export const updateRecipient = async (id, payload) => {
  const { data } = await api.patch(`/api/recipients/${id}`, payload)
  return data
}

export const deleteRecipient = async (id) => {
  const { data } = await api.delete(`/api/recipients/${id}`)
  return data
}

export const getRecipientTrackingStats = async (email) => {
  const { data } = await api.get('/api/recipients/tracking', { params: { email } })
  return data
}

// Historique des recherches de prospects
export const getProspectHistory = async () => {
  const { data } = await api.get('/api/prospects/searches/history')
  if (data && data.success === false) {
    throw new Error(data.message || 'Erreur /prospects/searches/history')
  }
  return data
}

// Enrichir des prospects
export const enrichProspects = async (requestId, contactIds, quantity, options = {}) => {
  const payload = {
    requestId,
    contactIds,
    quantity,
    requireEmail: options.requireEmail ?? false,
    revealEmails: options.revealEmails ?? true,
    revealPhones: options.revealPhones ?? true,
  }
  const { data } = await api.post('/api/prospects/enrich', payload)
  if (data && data.success === false) {
    throw new Error(data.message || 'Erreur enrichissement')
  }
  return data
}

// ============================
// Communication - Email Settings
// ============================

// Get email signature
export const getEmailSignature = async () => {
  const { data } = await api.get('/api/communication/signature')
  return data
}

// Update email signature
export const updateEmailSignature = async (signature) => {
  const { data } = await api.put('/api/communication/signature', { signature })
  return data
}

// Preview email with variables replaced
export const previewEmail = async (subject, body, recipient = {}, includeSignature = true) => {
  const { data } = await api.post('/api/communication/preview', {
    subject,
    body,
    recipient,
    includeSignature
  })
  return data
}

// ============================
// Scheduled Emails
// ============================

// Get scheduled emails
export const getScheduledEmails = async (status = null, limit = 100) => {
  const params = { limit }
  if (status) params.status = status
  const { data } = await api.get('/api/communication/scheduled', { params })
  return data
}

// Get scheduled emails stats
export const getScheduledEmailsStats = async () => {
  const { data } = await api.get('/api/communication/scheduled/stats')
  return data
}

// Schedule a single email
export const scheduleEmail = async (emailData) => {
  const { data } = await api.post('/api/communication/scheduled', emailData)
  return data
}

// Schedule bulk emails
export const scheduleBulkEmails = async (recipients, subject, body, scheduledAt, campaignName = null, timezone = null) => {
  const { data } = await api.post('/api/communication/scheduled/bulk', {
    recipients,
    subject,
    body,
    scheduledAt,
    campaignName,
    timezone
  })
  return data
}

// Cancel a scheduled email
export const cancelScheduledEmail = async (id) => {
  const { data } = await api.delete(`/api/communication/scheduled/${id}`)
  return data
}

// Update a scheduled email
export const updateScheduledEmail = async (id, updates) => {
  const { data } = await api.patch(`/api/communication/scheduled/${id}`, updates)
  return data
}

// ============================
// Auto Projects
// ============================

const normalizeCollectionResponse = (data) => {
  if (Array.isArray(data)) {
    return data
  }

  if (data && Array.isArray(data.member)) {
    return data.member
  }

  if (data && Array.isArray(data['hydra:member'])) {
    return data['hydra:member']
  }

  if (data && typeof data === 'object') {
    return [data]
  }

  return []
}

const normalizeNotificationsResponse = (data) => {
  const items = normalizeCollectionResponse(data)

  return {
    notifications: items.map((item) => ({
      id: item.id ?? item['@id']?.split('/').pop() ?? item.externalLink ?? `${item.title ?? 'notification'}-${item.createdAt ?? Date.now()}`,
      title: item.title ?? '',
      message: item.message ?? '',
      createdAt: item.createdAt ?? item.created_at ?? null,
      externalLink: item.externalLink ?? item.external_link ?? null,
      notifRead: Boolean(item.notifRead ?? item.notif_read),
    })),
    totalItems: Number(data?.totalItems ?? items.length),
    pagination: data?.view ?? data?.['hydra:view'] ?? {},
  }
}

export const getNotifications = async ({ page = 1, itemsPerPage = 10, notifRead = null } = {}) => {
  const params = {
    page,
    itemsPerPage,
    'order[createdAt]': 'desc',
  }

  if (notifRead !== null && notifRead !== undefined) {
    params.notifRead = notifRead ? 'true' : 'false'
  }

  const { data } = await api.get('/api/notifications', {
    params,
    headers: {
      'x-api-key': AUTO_PROJECT_API_KEY,
    },
  })

  return normalizeNotificationsResponse(data)
}

export const markNotificationAsRead = async (id) => {
  const { data } = await api.patch(
    `/api/notifications/${id}`,
    { notifRead: true },
    {
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'x-api-key': AUTO_PROJECT_API_KEY,
      },
    }
  )

  return data
}

export const deleteNotification = async (id) => {
  const { data } = await api.delete(`/api/notifications/${id}`, {
    headers: {
      'x-api-key': AUTO_PROJECT_API_KEY,
    },
  })
  return data
}

export const getAutoProjectAgencies = async () => {
  const { data } = await api.get('/api/guy_agencies', {
    headers: {
      'x-api-key': AUTO_PROJECT_API_KEY,
    },
  })
  return normalizeCollectionResponse(data)
}

export const createAgency = async (payload) => {
  const { data } = await api.post('/api/guy_agencies', payload, {
    headers: { 'x-api-key': AUTO_PROJECT_API_KEY },
  })
  return data
}

export const getAgency = async (id) => {
  const { data } = await api.get(`/api/guy_agencies/${id}`, {
    headers: { 'x-api-key': AUTO_PROJECT_API_KEY },
  })
  return data
}

export const updateAgency = async (id, payload) => {
  const { data } = await api.patch(`/api/guy_agencies/${id}`, payload, {
    headers: {
      'x-api-key': AUTO_PROJECT_API_KEY,
      'Content-Type': 'application/merge-patch+json',
    },
  })
  return data
}

export const deleteAgency = async (id) => {
  await api.delete(`/api/guy_agencies/${id}`, {
    headers: { 'x-api-key': AUTO_PROJECT_API_KEY },
  })
}

export const getAutoProjectStats = async () => {
  const { data } = await api.get('/api/auto-projects/stats', {
    headers: {
      'x-api-key': AUTO_PROJECT_API_KEY,
    },
  })
  return data
}

export const createAutoProject = async (payload) => {
  // Secured by the session JWT (auto-attached by the request interceptor),
  // not by the auto-project API key.
  const { data } = await api.post('/api/projects/manual', payload, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

export const updateAutoProjectField = async (projectId, payload) => {
  const { data } = await api.patch(`/api/projects/${encodeURIComponent(projectId)}`, payload, {
    headers: {
      'Content-Type': 'application/merge-patch+json',
      'x-api-key': AUTO_PROJECT_API_KEY,
    },
  })
  return data
}

export const assignAutoProjectSalesRep = async (projectId, userId) => {
  const { data } = await api.patch(`/api/projects/${encodeURIComponent(projectId)}/assigned-sales-rep`, { userId }, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

// Marquer / réouvrir un prospect « perdu » (motif obligatoire).
export const markProspectLost = async (id, reasonLostId) => {
  const { data } = await api.post(`/api/prospects/${id}/mark-lost`, { reasonLostId })
  return data
}
export const unmarkProspectLost = async (id) => {
  const { data } = await api.post(`/api/prospects/${id}/unmark-lost`)
  return data
}

// Catalogue des motifs de perte (partagé auto-projects / prospects).
export const getReasonLosts = async () => {
  const { data } = await api.get('/api/reason_losts')
  const items = data?.['hydra:member'] ?? data?.member ?? (Array.isArray(data) ? data : [])
  return items.map((r) => ({ id: r.id, value: r.value }))
}

// ============================
// Newsletter - Lists
// ============================

// Get all newsletter lists
export const getNewsletterLists = async () => {
  const { data } = await api.get('/api/newsletter/lists')
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/lists')
  return data // {success, count, lists: [...]}
}

// Get single newsletter list
export const getNewsletterList = async (listId) => {
  const { data } = await api.get(`/api/newsletter/lists/${listId}`)
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/lists/:id')
  return data // {success, list, stats}
}

// Create newsletter list
export const createNewsletterList = async (payload) => {
  const { data } = await api.post('/api/newsletter/lists', payload)
  if (data && data.success === false) throw new Error(data.message || 'Erreur création liste')
  return data // {success, list}
}

// Update newsletter list
export const updateNewsletterList = async (listId, payload) => {
  const { data } = await api.patch(`/api/newsletter/lists/${listId}`, payload)
  if (data && data.success === false) throw new Error(data.message || 'Erreur mise à jour liste')
  return data // {success, list}
}

// Delete newsletter list
export const deleteNewsletterList = async (listId) => {
  const { data } = await api.delete(`/api/newsletter/lists/${listId}`)
  if (data && data.success === false) throw new Error(data.message || 'Erreur suppression liste')
  return data // {success: true}
}

// ============================
// Newsletter - Stats & Abonnés
// ============================

// Stats globales Newsletter
export const getNewsletterStats = async () => {
  const { data } = await api.get('/api/newsletter/stats')
  // Attendu: { success, stats }
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/stats')
  return data
}

// Abonnés d'une liste (pagination simple)
export const getNewsletterSubscribers = async (listId, page = 1, perPage = 100, query = '') => {
  const { data } = await api.get(`/api/newsletter/subscribers`, {
    params: { list_id: listId, page, per_page: perPage, query },
  })
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/subscribers')
  return data
}

// Supprimer un abonné
export const deleteNewsletterSubscriber = async (subscriberId) => {
  await api.delete(`/api/newsletter/subscribers/${subscriberId}`)
  return true
}

// Créer un abonné manuellement
export const createNewsletterSubscriber = async (listId, email, name = '') => {
  const { data } = await api.post('/api/newsletter/subscribers', { list_id: listId, email, name })
  if (data && data.success === false) throw new Error(data.message || 'Erreur création abonné')
  return data
}

// Synchroniser les prospects vers une liste
export const syncProspectsToNewsletter = async (listId, prospectIds = null) => {
  const body = { list_id: listId }
  if (Array.isArray(prospectIds) && prospectIds.length > 0) body.prospect_ids = prospectIds
  const { data } = await api.post('/api/newsletter/subscribers/sync-prospects', body)
  if (data && data.success === false) throw new Error(data.message || 'Erreur sync-prospects')
  return data
}

// Récupérer tous les prospects synchronisés de l'utilisateur (filtrés par owner_user_id)
export const getMyProspectSubscribers = async (page = 1, perPage = 100, query = '') => {
  const { data } = await api.get('/api/newsletter/subscribers/my-prospects', {
    params: { page, per_page: perPage, query },
  })
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/subscribers/my-prospects')
  return data
}

// ============================
// Newsletter - Campagnes & Templates
// ============================

// Templates d'emailing
export const getNewsletterTemplates = async () => {
  const { data } = await api.get('/api/newsletter/templates')
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/templates')
  return data
}

// Campagnes: liste
export const getNewsletterCampaigns = async (page = 1, perPage = 100, status = '') => {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
  if (status) params.set('status', status)
  const { data } = await api.get(`/api/newsletter/campaigns?${params.toString()}`)
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/campaigns')
  return data
}

// Get single campaign
export const getNewsletterCampaign = async (campaignId) => {
  const { data } = await api.get(`/api/newsletter/campaigns/${campaignId}`)
  if (data && data.success === false) throw new Error(data.message || 'Erreur /newsletter/campaigns/:id')
  return data
}

// Créer une campagne
export const createNewsletterCampaign = async (payload) => {
  const { data } = await api.post('/api/newsletter/campaigns', payload)
  if (data && data.success === false) throw new Error(data.message || 'Erreur création campagne')
  return data
}

// Update campaign
export const updateNewsletterCampaign = async (campaignId, payload) => {
  const { data } = await api.patch(`/api/newsletter/campaigns/${campaignId}`, payload)
  if (data && data.success === false) throw new Error(data.message || 'Erreur mise à jour campagne')
  return data
}

// Changer le statut d'une campagne
export const updateNewsletterCampaignStatus = async (campaignId, status) => {
  const { data } = await api.put(`/api/newsletter/campaigns/${campaignId}/status`, { status })
  if (data && data.success === false) throw new Error(data.message || 'Erreur statut campagne')
  return data
}

// Supprimer une campagne
export const deleteNewsletterCampaign = async (campaignId) => {
  const { data } = await api.delete(`/api/newsletter/campaigns/${campaignId}`)
  if (data && data.success === false) throw new Error(data.message || 'Erreur suppression campagne')
  return data
}

// Envoyer un email de test
export const testNewsletterCampaign = async (campaignId, emails) => {
  const { data } = await api.post(`/api/newsletter/campaigns/${campaignId}/test`, { emails })
  if (data && data.success === false) throw new Error(data.message || 'Erreur test campagne')
  return data
}

// ============================
// Email Sequences
// ============================

// Get all sequences
export const getEmailSequences = async () => {
  const { data } = await api.get('/api/email_sequences')
  return data
}

// Get single sequence with steps
export const getEmailSequence = async (id) => {
  const { data } = await api.get(`/api/email_sequences/${id}`)
  return data
}

// Create sequence
export const createEmailSequence = async (payload) => {
  const { data } = await api.post('/api/email_sequences', payload)
  return data
}

// Update sequence (PATCH for partial updates)
export const updateEmailSequence = async (id, payload) => {
  const { data } = await api.patch(`/api/email_sequences/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  })
  return data
}

// Delete sequence
export const deleteEmailSequence = async (id) => {
  const { data } = await api.delete(`/api/email_sequences/${id}`)
  return data
}

// ============================
// Sequence Enrollments
// ============================

// Get enrollments for a sequence (paginated)
export const getSequenceEnrollments = async (sequenceId, options = {}) => {
  const {
    status = null,
    q = null,
    page = 1,
    limit = 50,
    sort = 'enrolledAt',
    direction = 'desc',
    recipientOnly = false,
    prospectOnly = false
  } = options
  const params = { page, limit, sort, direction }
  if (status) params.status = status
  if (q) params.q = q
  if (recipientOnly) params.recipientOnly = true
  if (prospectOnly) params.prospectOnly = true
  const { data } = await api.get(`/api/email_sequences/${sequenceId}/enrollments`, { params })
  return data
}

// Enrollment stats for a sequence
export const getSequenceEnrollmentStats = async (sequenceId) => {
  const { data } = await api.get(`/api/email_sequences/${sequenceId}/enrollments/stats`)
  return data
}

// Enroll prospects in a sequence
export const enrollProspectsInSequence = async (sequenceId, prospectIds) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/bulk`, {
    prospectIds
  })
  return data
}

// Enroll a single prospect (or reset if already enrolled and forceReset = true)
export const enrollProspectInSequence = async (sequenceId, prospectId, forceReset = false) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/single`, {
    prospectId,
    forceReset
  })
  return data
}

// Enroll a recipient in a sequence (email-only)
export const enrollRecipientInSequence = async (sequenceId, payload) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/recipient`, payload)
  return data
}

// Enroll multiple recipients in a sequence (bulk)
// recipients: Array of { email, firstName?, lastName? }
// options: { forceReset?, groupId?, createGroupName? }
export const bulkEnrollRecipients = async (sequenceId, recipients, options = {}) => {
  const { forceReset = false, groupId = null, createGroupName = null } = options
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/recipients/bulk`, {
    recipients,
    forceReset,
    ...(groupId && { groupId }),
    ...(createGroupName && { createGroupName })
  })
  return data
}

// Update enrollment status  
export const updateEnrollmentStatus = async (enrollmentId, status) => {
  const { data } = await api.put(`/api/sequence_enrollments/${enrollmentId}`, { status })
  return data
}

// Remove enrollment
export const removeEnrollment = async (enrollmentId) => {
  const { data } = await api.delete(`/api/sequence_enrollments/${enrollmentId}`)
  return data
}

// Remove enrollment from a sequence (explicit)
export const removeEnrollmentFromSequence = async (sequenceId, enrollmentId) => {
  const { data } = await api.delete(`/api/email_sequences/${sequenceId}/enrollments/${enrollmentId}`)
  return data
}

// Bulk remove enrollments from a sequence
export const bulkRemoveEnrollments = async (sequenceId, enrollmentIds) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/bulk-remove`, { enrollmentIds })
  return data
}

// Remove all enrollments from a sequence where recipients belong to a specific group
export const removeRecipientGroupFromSequence = async (sequenceId, groupId) => {
  const { data } = await api.delete(`/api/email_sequences/${sequenceId}/enrollments/recipient-group/${groupId}`)
  return data
}

// Enroll recipient groups in a sequence
// groupIds: Array of group IDs to enroll
export const enrollRecipientGroups = async (sequenceId, groupIds, forceReset = false) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/recipient-groups/bulk`, {
    groupIds,
    forceReset
  })
  return data
}

// Reset enrollment to step 0
export const resetEnrollment = async (enrollmentId) => {
  const { data } = await api.post(`/api/sequence_enrollments/${enrollmentId}/reset`)
  return data
}

// Admin update for testing (status/currentStep/currentNodeId/nextStepAt/reset counters)
export const updateEnrollmentAdmin = async (enrollmentId, payload) => {
  const { data } = await api.post(`/api/sequence_enrollments/${enrollmentId}/admin`, payload)
  return data
}

// ============================
// Email Tracking
// ============================

// Get email logs with tracking stats
export const getEmailLogs = async (page = 1, limit = 50) => {
  const { data } = await api.get('/api/email_logs', { params: { page, itemsPerPage: limit } })
  return data
}

// Campaign aggregations (sequence)
export const getEmailLogCampaignsSequence = async (options = {}) => {
  const { page = 1, limit = 25, q = null, from = null, to = null } = options
  const params = { page, limit }
  if (q) params.q = q
  if (from) params.from = from
  if (to) params.to = to
  const { data } = await api.get('/api/email_logs/campaigns/sequence', { params })
  return data
}

// Campaign aggregations (bulk)
export const getEmailLogCampaignsBulk = async (options = {}) => {
  const { page = 1, limit = 25, q = null, from = null, to = null } = options
  const params = { page, limit }
  if (q) params.q = q
  if (from) params.from = from
  if (to) params.to = to
  const { data } = await api.get('/api/email_logs/campaigns/bulk', { params })
  return data
}

// Campaign detail logs (sequence)
export const getEmailLogCampaignSequenceLogs = async (sequenceId, options = {}) => {
  const { page = 1, limit = 50, status = null } = options
  const params = { page, limit }
  if (status) params.status = status
  const { data } = await api.get(`/api/email_logs/campaigns/sequence/${sequenceId}`, { params })
  return data
}

// Campaign detail logs (bulk)
export const getEmailLogCampaignBulkLogs = async (batchId, options = {}) => {
  const { page = 1, limit = 50, status = null } = options
  const params = { page, limit }
  if (status) params.status = status
  const { data } = await api.get(`/api/email_logs/campaigns/bulk/${batchId}`, { params })
  return data
}

// Get single email log details
export const getEmailLog = async (id) => {
  const { data } = await api.get(`/api/email_logs/${id}`)
  return data
}

// Get tracking stats for a specific tracking ID
export const getTrackingStats = async (trackingId) => {
  const { data } = await api.get(`/api/t/stats/${trackingId}`)
  return data
}

// Send email with tracking (uses Gmail send endpoint which should create EmailLog)
export const sendEmailWithTracking = async (to, subject, body) => {
  const { data } = await api.post('/api/gmail/send', { to, subject, body })
  return data
}

// Attach Authorization header if token exists
api.interceptors.request.use(
  (config) => {
    try {
      const headers = config.headers || {}
      const hasAutoProjectApiKey = Boolean(
        headers['x-api-key'] ||
        headers['X-API-Key'] ||
        headers['X-API-KEY'] ||
        (typeof headers.get === 'function' && headers.get('x-api-key'))
      )

      if (hasAutoProjectApiKey) {
        return config
      }

      const token = useAuthStore.getState()?.token || localStorage.getItem('auth_token')
      if (token) {
        config.headers = headers
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (_) {
      // noop
    }
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    if (status === 401) {
      try {
        useAuthStore.getState()?.logout?.()
      } catch (_) {
        // noop
      }
      // Avoid redirect loop if already on /login
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ============================
// Prospect Groups
// ============================

// Get all prospect groups
export const getProspectGroups = async () => {
  const { data } = await api.get('/api/prospect_groups')
  return data
}

// Get single prospect group
export const getProspectGroup = async (id) => {
  const { data } = await api.get(`/api/prospect_groups/${id}`)
  return data
}

// Create prospect group
export const createProspectGroup = async (payload) => {
  const { data } = await api.post('/api/prospect_groups', payload)
  return data
}

// Update prospect group (PATCH for partial updates)
export const updateProspectGroup = async (id, payload) => {
  const { data } = await api.patch(`/api/prospect_groups/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  })
  return data
}

// Delete prospect group
export const deleteProspectGroup = async (id) => {
  const { data } = await api.delete(`/api/prospect_groups/${id}`)
  return data
}

// Enroll a group in a sequence
export const enrollGroupInSequence = async (sequenceId, groupId) => {
  const { data } = await api.post(`/api/email_sequences/${sequenceId}/enrollments/group`, {
    groupId
  })
  return data
}

// ============================
// Recipient Groups
// ============================

// Get all recipient groups
export const getRecipientGroups = async () => {
  const { data } = await api.get('/api/recipient_groups')
  return data
}

// Get single recipient group
export const getRecipientGroup = async (id) => {
  const { data } = await api.get(`/api/recipient_groups/${id}`)
  return data
}

// Create recipient group
export const createRecipientGroup = async (payload) => {
  const { data } = await api.post('/api/recipient_groups', payload)
  return data
}

// Update recipient group (PATCH for partial updates)
export const updateRecipientGroup = async (id, payload) => {
  const { data } = await api.patch(`/api/recipient_groups/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' }
  })
  return data
}

// Delete recipient group
export const deleteRecipientGroup = async (id) => {
  const { data } = await api.delete(`/api/recipient_groups/${id}`)
  return data
}

// Get members of a recipient group (paginated)
export const getRecipientGroupMembers = async (groupId, options = {}) => {
  const { page = 1, limit = 50, q = null } = options
  const params = new URLSearchParams({ page, limit })
  if (q) params.append('q', q)
  const { data } = await api.get(`/api/recipient_groups/${groupId}/members?${params}`)
  return data
}

// Remove a recipient from a group
export const removeRecipientFromGroup = async (groupId, recipientId) => {
  const { data } = await api.delete(`/api/recipient_groups/${groupId}/members/${recipientId}`)
  return data
}

// Add recipients to a group
export const addRecipientsToGroup = async (groupId, recipientIds) => {
  const { data } = await api.post(`/api/recipient_groups/${groupId}/members`, { recipientIds })
  return data
}

// ============================
// Sequence Test/Debug Endpoints (Pre-prod only)
// ============================

// Get sequence info for debugging
export const getSequenceDebugInfo = async (sequenceId) => {
  const { data } = await api.get(`/api/test/sequence/${sequenceId}/info`)
  return data
}

// Get enrollment status for debugging
export const getEnrollmentDebugStatus = async (enrollmentId) => {
  const { data } = await api.get(`/api/test/enrollment/${enrollmentId}/status`)
  return data
}

// Execute next step for an enrollment (manual trigger)
export const executeEnrollmentStep = async (enrollmentId) => {
  const { data } = await api.post(`/api/test/sequence/${enrollmentId}`)
  return data
}

// Reset tracking counters for an enrollment
export const resetEnrollmentTracking = async (enrollmentId) => {
  const { data } = await api.post(`/api/test/enrollment/${enrollmentId}/reset-tracking`)
  return data
}

// Run all pending enrollments for a sequence
export const runAllSequenceEnrollments = async (sequenceId) => {
  const { data } = await api.post(`/api/test/sequence/${sequenceId}/run-all`)
  return data
}

// Quick enroll a prospect (creates and executes first step)
export const quickEnrollProspect = async (sequenceId, prospectId) => {
  const { data } = await api.post(`/api/test/sequence/${sequenceId}/quick-enroll/${prospectId}`)
  return data
}

// ─── Immobilier ──────────────────────────────────────────────────────────────

// ── Scraper Python direct (Coolify) ───────────────────────────────────────

// Lance un job de scraping asynchrone (POST /api/scrape).
// Retourne { job_id, status }.
export const startScraperJob = async (localisation, nbPages = 2) => {
  const params = { nb_pages: nbPages }
  if (localisation) params.localisation = localisation
  const { data } = await scraperApi.post('/api/scrape', null, { params })
  if (data && data.detail) throw new Error(data.detail)
  return data
}

// Récupère le statut d'un job (GET /api/scrape/status/{job_id}).
export const getScraperJobStatus = async (jobId) => {
  const { data } = await scraperApi.get(`/api/scrape/status/${encodeURIComponent(jobId)}`)
  return data
}

// Récupère les annonces après scraping (GET /api/annonces).
// Mappe les noms de filtres frontend → noms de champs Python.
export const fetchScraperAnnonces = async (filters, page = 1) => {
  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s
  const params = { page, limit: Math.min((filters.nbPages || 2) * 25, 100) }
  if (filters.location) params.ville = filters.location
  if (filters.propertyType) params.type_bien = capitalize(filters.propertyType)
  if (filters.transactionType) params.type_transaction = capitalize(filters.transactionType)
  if (filters.priceMin != null) params.prix_min = filters.priceMin
  if (filters.priceMax != null) params.prix_max = filters.priceMax
  if (filters.surfaceMin != null) params.surface_min = filters.surfaceMin
  if (filters.surfaceMax != null) params.surface_max = filters.surfaceMax
  if (filters.dateFrom) params.date_min = filters.dateFrom
  if (filters.dateTo) params.date_max = filters.dateTo
  const { data } = await scraperApi.get('/api/annonces', { params })
  // Normalise vers { annonces, total } attendus par le store
  return {
    annonces: data.annonces ?? data.items ?? [],
    total: data.total ?? 0,
  }
}

// Annule un job de scraping en cours (fire-and-forget via le backend PHP).
export const cancelScraperJob = async (jobId) => {
  try {
    await api.post(`/api/immobilier/cancel/${encodeURIComponent(jobId)}`)
  } catch {
    // Non-bloquant : l'API externe ne supporte peut-être pas l'annulation
  }
}

// Enregistre la recherche côté PHP après scraping Python direct.
// Déduit 1 crédit et retourne { searchId } pour le flow claim.
export const registerImmobilierSearch = async (filters, total) => {
  const { data } = await api.post('/api/immobilier/register-search', { filters, total })
  if (data && data.success === false) throw new Error(data.error || 'Erreur enregistrement recherche')
  return data
}

// ── Scraper PHP backend (legacy — conservé pour claim/history) ─────────────

// Démarre un job de scraping (coût : 1 crédit). Retourne { jobId, searchId }.
export const startImmobilierScrape = async (filters) => {
  const { data } = await api.post('/api/immobilier/scrape', { filters })
  if (data && data.success === false) throw new Error(data.error || 'Erreur démarrage scraping')
  return data
}

// Récupère le statut d'un job de scraping.
export const getImmobilierScrapeStatus = async (jobId) => {
  const { data } = await api.get(`/api/immobilier/scrape-status/${encodeURIComponent(jobId)}`)
  return data
}

// Récupère les annonces scrapées avec filtres (appelé quand job = done).
export const fetchImmobilierAnnonces = async (filters, searchId = null) => {
  const params = {}
  if (filters.location) params.location = filters.location
  if (filters.propertyType) params.propertyType = filters.propertyType
  if (filters.transactionType) params.transactionType = filters.transactionType
  if (filters.priceMin != null) params.priceMin = filters.priceMin
  if (filters.priceMax != null) params.priceMax = filters.priceMax
  if (filters.surfaceMin != null) params.surfaceMin = filters.surfaceMin
  if (filters.surfaceMax != null) params.surfaceMax = filters.surfaceMax
  if (filters.dateFrom) params.dateFrom = filters.dateFrom
  if (filters.dateTo) params.dateTo = filters.dateTo
  if (filters.quantity) params.quantity = filters.quantity
  if (searchId) params.searchId = searchId
  const { data } = await api.get('/api/immobilier/annonces', { params })
  if (data && data.success === false) throw new Error(data.error || 'Erreur récupération annonces')
  return data
}

// Sauvegarde les annonces sélectionnées (coût : 2 crédits/annonce).
export const claimImmobilier = async (searchId, annonces) => {
  const { data } = await api.post('/api/immobilier/claim', { searchId, annonces })
  if (data && data.success === false) throw new Error(data.error || 'Erreur acquisition annonces')
  return data
}

export const getImmobilierHistory = async () => {
  const { data } = await api.get('/api/immobilier/history')
  if (data && data.success === false) throw new Error(data.error || 'Erreur historique immobilier')
  return data
}

export const getMyImmobilierAnnonces = async () => {
  const { data } = await api.get('/api/immobilier/my-annonces')
  return data
}

// ─── Organisation ────────────────────────────────────────────────────────────

// Récupère l'organisation de l'utilisateur courant
export const getMyOrganization = async () => {
  const { data } = await api.get('/api/auth/organization')
  return data
}

// Séquence de relance des projets auto de l'organisation (null = aucune).
export const setAutoProjectRelaunchSequence = async (sequenceId) => {
  const { data } = await api.put('/api/crm/auto-project-relaunch-sequence', { sequenceId })
  return data
}

// Crée une organisation et en fait le courant l'owner
export const createOrganization = async ({ name, slug }) => {
  const { data } = await api.post('/api/organizations', { name, slug })
  return data
}

// Invite un utilisateur dans son organisation (owner seulement)
export const inviteToOrganization = async (email) => {
  const { data } = await api.post('/api/organizations/invite', { email })
  return data
}

// Retire un membre de l'organisation
export const removeMemberFromOrganization = async (memberId) => {
  const { data } = await api.delete(`/api/organizations/members/${memberId}`)
  return data
}

// --- Commentaires internes -------------------------------------------------
// Authentifiés par JWT (pas de x-api-key) : le serveur a besoin de savoir qui
// écrit pour attribuer l'auteur du message.

export const getInternalComments = async ({ autoProjectId, prospectId }) => {
  const { data } = await api.get('/api/internal-comments', {
    params: autoProjectId ? { autoProjectId } : { prospectId },
  })
  return data.comments || []
}

export const createInternalComment = async ({ autoProjectId, prospectId, body }) => {
  const { data } = await api.post('/api/internal-comments', {
    ...(autoProjectId ? { autoProjectId } : { prospectId }),
    body,
  })
  return data.comment
}

export const deleteInternalComment = async (commentId) => {
  await api.delete(`/api/internal-comments/${commentId}`)
}

export default api
